import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { CreativeAiScope } from '../api/CreativeAiApi'
import { creativeLabelEditApi } from '../api/CreativeLabelEditApi'
import type { CreativeKind } from '../models/analysis.types'
import {
  buildLabelOverrideMap,
  EMPTY_LABEL_OVERRIDE_MAP,
  type LabelOverrideMap,
} from '../service/labelOverrides'

function cacheKey(scope: CreativeAiScope, kind: CreativeKind): string {
  return `${String(scope.accountId)}:${String(scope.workspaceId)}:${kind}`
}

export const useCreativeLabelOverrideStore = defineStore('creativeAiLabelOverride', () => {
  const maps = ref(new Map<string, LabelOverrideMap>())
  const inFlight = new Map<string, Promise<void>>()

  function overridesFor(scope: CreativeAiScope, kind: CreativeKind): LabelOverrideMap {
    return maps.value.get(cacheKey(scope, kind)) ?? EMPTY_LABEL_OVERRIDE_MAP
  }

  async function load(
    scope: CreativeAiScope,
    kind: CreativeKind,
    options: { force?: boolean } = {},
  ): Promise<void> {
    const key = cacheKey(scope, kind)
    const pending = inFlight.get(key)
    if (pending) return pending
    if (!options.force && maps.value.has(key)) return
    const request = (async () => {
      try {
        const rows = await creativeLabelEditApi.listLabelOverrides(scope, kind)
        const next = new Map(maps.value)
        next.set(key, buildLabelOverrideMap(rows))
        maps.value = next
      } catch {
        if (!maps.value.has(key)) {
          const next = new Map(maps.value)
          next.set(key, EMPTY_LABEL_OVERRIDE_MAP)
          maps.value = next
        }
      } finally {
        inFlight.delete(key)
      }
    })()
    inFlight.set(key, request)
    return request
  }

  function reset(): void {
    maps.value = new Map()
    inFlight.clear()
  }

  return { maps, overridesFor, load, reset }
})

export type CreativeLabelOverrideStore = ReturnType<typeof useCreativeLabelOverrideStore>
